import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { preparationsApi } from "@/api/interventionClient";
import Preparation from "@/models/Preparation";
import Agency from "@/models/SvcModels/Agency";
import PreparationWithAnalysis from "@/models/PreparationWithAnalysis";
import { useMaterialStore } from "@/store/modules/material";
import { useMissionStore } from "@/store/modules/mission";

const PreparationWithAnalysisRepo = useRepo(PreparationWithAnalysis);
const PreparationRepo = useRepo(Preparation);

export const usePreparationStore = defineStore("preparation", {
  state: () => ({
    currentPreparationUuid: null,
  }),

  actions: {
    async resetCurrentPreparationUuid() {
      this.currentPreparationUuid = null;
    },
    async retrievePreparationByMission(missionUuid) {
      if (this.currentPreparationUuid) {
        return PreparationRepo.find(this.currentPreparationUuid);
      }
      const response = await preparationsApi.list({ project: missionUuid });

      const data = response.results;
      if (data.length > 0) {
        const preparationData = data[0];
        const materialStore = useMaterialStore();
        await materialStore.fetchAllMaterials({
          uuids: preparationData.validatedMaterials,
        });
        PreparationRepo.save(preparationData);
        this.setCurrentPreparationUuid(preparationData.uuid);
        return preparationData;
      }
    },

    async fetchValidatedMaterials() {
      const preparation = this.getCurrentPreparation;
      const uuids = preparation?.validatedMaterials;
      if (!uuids?.length) return null;
      if (uuids.length === preparation.validatedMaterialsData?.length) {
        return null;
      }

      const materialStore = useMaterialStore();
      return await materialStore.fetchAllMaterials({ uuids });
    },

    async retrievePreparationWithAnalysisSynthesis(preparationUuid) {
      const response = await preparationsApi.retrieve({
        uuid: preparationUuid,
      });
      return PreparationWithAnalysisRepo.make(response);
    },

    async updatePreparation(preparation) {
      const missionStore = useMissionStore();
      const { uuid, ...preparationRequest } = preparation;
      const response = await preparationsApi.update({
        uuid,
        preparationRequest: {
          ...preparationRequest,
          project: missionStore.currentMissionUuid,
        },
      });

      PreparationRepo.save(response);
    },

    async createPreparation(preparation) {
      const missionStore = useMissionStore();
      const response = await preparationsApi.create({
        preparationRequest: {
          ...preparation,
          project: missionStore.currentMissionUuid,
        },
      });

      PreparationRepo.save(response);
      this.setCurrentPreparationUuid(response.uuid);
    },

    setCurrentPreparationUuid(preparationUuid) {
      this.currentPreparationUuid = preparationUuid;
    },
  },

  getters: {
    getCurrentPreparationUuid: (state) => state.currentPreparationUuid,
    getCurrentPreparation: (state) => {
      return (
        PreparationRepo.where("uuid", state.currentPreparationUuid)
          .with("validatedMaterialsData", (query) => {
            query.with("typologies");
          })
          .with("materialsData")
          .with("analysisData")
          .withAllRecursive()
          .get()[0] || {}
      );
    },
    getCurrentPreparationWithMaterialFiltered:
      (state) =>
      ({ form, closurefunc }) => {
        return (
          PreparationRepo.where("uuid", state.currentPreparationUuid)
            .with("materialsData", (query) => {
              query.where((material) => {
                if (form.agency?.name) {
                  const agency = Agency.find(material.agencyUuid);
                  return (
                    closurefunc(form, material) &&
                    agency.name.toLowerCase().includes(form.agency.name.toLowerCase())
                  );
                }
                return closurefunc(form, material);
              });
            })
            .withAllRecursive()
            .get()[0] || {}
        );
      },
    getCurrentPreparationWithAdditionalMaterialFiltered:
      (state) =>
      ({ form, closurefunc }) => {
        return (
          PreparationRepo.where("uuid", state.currentPreparationUuid)
            .with("additionalsMaterial", (query) => {
              query.where((material) => {
                return closurefunc(form, material);
              });
            })
            .withAllRecursive()
            .get()[0] || {}
        );
      },
  },
});
